import {
  CloudArrowDown,
  Integrations,
  Layout,
  Plus,
  RelatedObjects,
} from '@sweep-io/sweep-design/dist/icons';
import { ActionsMenu, MenuPlacement } from '../../../../common/actions-menu/ActionsMenu';
import { importFunnelPanelType } from '../../../funnel-map-flow/dialogs/import-funnel/importFunnelPanelType';
import { metadataElementsPanelType } from '../../../funnel-map-flow/dialogs/metadata-elements/metadataElementsPanelType';
import { useHandleCommentsAndAiFirst } from './useHandleCommentsAndAiFirst';
import { usePanels } from '../../../../panels/usePanels';
import { openNewFunnelDialog } from '../../../../../reducers/newFunnelReducer';
import { useDispatch } from 'react-redux';
import { useConfigurationCanvasContext } from '../../../configuration-canvas/ConfigurationCanvasContext';
import { useCheckAllPermissionsForCurrentCrmOrg } from '../../../../common/permissions/useCheckAllPermissionsForCurrentCrmOrg';
import { templatesPanelType } from '../../../funnel-map-flow/templates/dialog/panelTypes';
import { setAddOrImportDialogOpen } from '../../../../third-party-funnels/thirdPartyFunnelsReducer';
import { PopoverOrigin } from '@mui/material';
import { useFeatureToggle } from '../../../../../feature-flags/useFeatureToggle';

export enum AddFunnelMenuActions {
  IMPORT_FUNNEL = 'import-funnel',
  NEW_FUNNEL = 'new-funnel',
  ADD_3TH_PARTY_FUNNEL = 'add-3th-party-funnel',
  TEMPLATES_AND_PLUGINS = 'templates-and-plugins',
  ADD_METADATA_ELEMENTS = 'add-metadata-elements',
}

const DEFAULT_ALLOWED_MENU_ACTIONS = [
  AddFunnelMenuActions.IMPORT_FUNNEL,
  AddFunnelMenuActions.NEW_FUNNEL,
  AddFunnelMenuActions.ADD_3TH_PARTY_FUNNEL,
  AddFunnelMenuActions.TEMPLATES_AND_PLUGINS,
  AddFunnelMenuActions.ADD_METADATA_ELEMENTS,
];
const TOOLBAR_ACTIONS_SX = {
  '& .MuiListItemIcon-root': {
    minWidth: 0,
    paddingRight: 1,
  },
  '& .MuiButtonBase-root': {
    paddingLeft: '8px',
    paddingRight: '8px',
  },
};

export const AddFunnelMenu = ({
  disabled,
  allowedMenuActions = DEFAULT_ALLOWED_MENU_ACTIONS,
  menuPlacement,
  anchorOrigin,
  transformOrigin,
  onClickMenuItemCallback,
}: {
  disabled: boolean;
  allowedMenuActions?: AddFunnelMenuActions[];
  menuPlacement?: MenuPlacement;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  onClickMenuItemCallback?: (action: AddFunnelMenuActions) => void;
}) => {
  const { canvasToolbar } = useFeatureToggle();
  const { canvasCrmOrgId } = useConfigurationCanvasContext();
  const { maybeOpenPanel } = usePanels();
  const handleCommentsAndAiFirst = useHandleCommentsAndAiFirst();
  const dispatch = useDispatch();
  const createFunnelsPermissions = useCheckAllPermissionsForCurrentCrmOrg([
    'create:funnels',
    'edit:funnels',
  ]);
  const actionsSx = canvasToolbar ? TOOLBAR_ACTIONS_SX : undefined;

  const baseActions = [
    {
      label: 'Add business process',
      value: AddFunnelMenuActions.IMPORT_FUNNEL,
      actionIcon: <CloudArrowDown size="medium" />,
      hidden: !allowedMenuActions.includes(AddFunnelMenuActions.IMPORT_FUNNEL),
      actionSx: actionsSx,
    },
    {
      label: 'Add Metadata Elements',
      value: AddFunnelMenuActions.ADD_METADATA_ELEMENTS,
      actionIcon: <RelatedObjects size="medium" />,
      disabled: !createFunnelsPermissions,
      disabledTooltip: 'To add new funnels, please contact your admin.',
      hidden: !allowedMenuActions.includes(AddFunnelMenuActions.ADD_METADATA_ELEMENTS),
      actionSx: actionsSx,
    },
    {
      label: 'Templates and plugins',
      value: AddFunnelMenuActions.TEMPLATES_AND_PLUGINS,
      actionIcon: <Layout size="medium" />,
      disabled: !createFunnelsPermissions,
      disabledTooltip: 'To add new funnels, please contact your admin.',
      hidden: !allowedMenuActions.includes(AddFunnelMenuActions.TEMPLATES_AND_PLUGINS),
      actionSx: actionsSx,
    },
    {
      label: 'External Processes',
      value: AddFunnelMenuActions.ADD_3TH_PARTY_FUNNEL,
      actionIcon: <Integrations size="medium" />,
      disabled: !createFunnelsPermissions,
      disabledTooltip: 'To add new funnels, please contact your admin.',
      hidden: !allowedMenuActions.includes(AddFunnelMenuActions.ADD_3TH_PARTY_FUNNEL),
      actionSx: actionsSx,
    },
  ];

  const actions = baseActions.filter((action) => !action.hidden);

  return (
    <ActionsMenu
      buttonTestId="add-funnel-menu-button"
      actions={actions}
      menuPlacement={menuPlacement}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      onClick={(action) => {
        onClickMenuItemCallback?.(action.value);
        switch (action.value) {
          case AddFunnelMenuActions.IMPORT_FUNNEL:
            handleCommentsAndAiFirst(() => {
              maybeOpenPanel({ panelType: importFunnelPanelType });
            });
            break;
          case AddFunnelMenuActions.NEW_FUNNEL:
            dispatch(openNewFunnelDialog({ crmOrgId: canvasCrmOrgId }));
            break;
          case AddFunnelMenuActions.ADD_3TH_PARTY_FUNNEL:
            dispatch(setAddOrImportDialogOpen('import'));
            break;
          case AddFunnelMenuActions.TEMPLATES_AND_PLUGINS:
            handleCommentsAndAiFirst(() => {
              maybeOpenPanel({ panelType: templatesPanelType });
            });
            break;
          case AddFunnelMenuActions.ADD_METADATA_ELEMENTS:
            handleCommentsAndAiFirst(() => {
              maybeOpenPanel({ panelType: metadataElementsPanelType });
            });
            break;

          default:
            break;
        }
      }}
      MenuButtonIcon={<Plus size="medium" />}
      iconSize="tiny"
      iconVariant="primary"
      disabled={disabled}
    />
  );
};
